// Provenance: repo:tds-reconciliation-portal:frontend/src/pages/AliasPage.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the page
// identity moved into the workspace frame's tab bar; the retire gate reads the SAME
// evaluation the API enforces with (lib/identity, mirroring compat.role_label).
//
// Name mapping: one worklist, every row a company, ranked by what is at stake.
// The unit is a company and the order is money. `NameMappingList` is the
// surface; this file supplies the run, the actions and the detail modal.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router'

import { ApiError, postJson } from '../../api/client'
import { useAliasGroups, usePeriodSummary } from '../../api/hooks'
import { useNameMapping, zonesOf } from '../../api/nameMapping'
import type { AliasGroup } from '../../api/types'
import { GroupDetail } from '../../components/AliasGroupDetail'
import {
  CategorySections,
  MappingHeader,
  RulesTab,
  WithheldBanner,
} from '../../components/NameMappingList'
import { NoRunLoaded } from '../../components/NoRunLoaded'
import { showToast } from '../../components/toast'
import { useEvaluation } from '../../lib/access'
import { roleLabelOf } from '../../lib/identity'
import { useRunSelection } from '../../workspace/runContext'

function useLifecycle(clientId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { id: string; verb: 'retire' | 'reinstate' }) =>
      postJson(`/alias-groups/${p.id}/${p.verb}`, {}),
    onSuccess: (_d, p) => {
      showToast({
        color: p.verb === 'retire' ? 'blue' : 'green',
        title: p.verb === 'retire'
          ? 'Alias retired — it no longer applies from the next run. Past runs are unchanged.'
          : 'Alias reinstated.',
      })
      void qc.invalidateQueries({ queryKey: ['alias-groups', clientId] })
      void qc.invalidateQueries({ queryKey: ['alias-suggestions'] })
      void qc.invalidateQueries({ queryKey: ['name-mapping'] })
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not change the alias',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })
}

/** Sign for the names a multi-party group reaches.
 *
 *  The same `approve` endpoint that signs off an imported group — approving IS
 *  recording the reach, so there is one action and not a second concept the
 *  reviewer has to learn. The message says the next run rather than this one:
 *  a run's alias snapshot is immutable, which is what makes past runs still
 *  mean what they meant.
 */
function useSignReach(clientId: string, runId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    // The run this screen is showing, and the names it showed. The server
    // re-measures both and refuses if they have moved since the page was
    // drawn: `approved_reach` is the only durable record that a wide merge
    // was agreed to, and it must name what the approver actually read.
    mutationFn: (g: AliasGroup) =>
      postJson<AliasGroup>(`/alias-groups/${g.id}/approve`, {
        run: runId,
        names_26as: g.names_26as ?? [],
        names_books: g.names_books ?? [],
      }),
    onSuccess: (d) => {
      // counted off what was STORED, not off what was on screen — a signature
      // for one run's names must not be confirmed with another run's counts
      const n26 = d.approved_reach?.names_26as?.length ?? 0
      const nbk = d.approved_reach?.names_books?.length ?? 0
      showToast({
        color: 'green',
        title: `Reach approved — ${n26} x ${nbk} names`,
        message: 'The group applies from the next run. Runs already made keep '
          + 'the mapping they used.',
      })
      void qc.invalidateQueries({ queryKey: ['alias-groups', clientId] })
      void qc.invalidateQueries({ queryKey: ['name-mapping'] })
    },
    onError: (e) =>
      showToast({
        color: 'red',
        title: 'Could not approve the reach',
        message: e instanceof ApiError ? e.message : String(e),
      }),
  })
}

export function AliasPage() {
  const params = useParams()
  const { evaluation } = useEvaluation()
  const role = roleLabelOf(evaluation)
  const summaryQ = usePeriodSummary(params.periodId ?? '')
  const clientId = params.clientId ?? summaryQ.data?.client.id ?? ''
  // the run THIS session has open, not the client's latest — the figures on
  // this page are all properties of one run's membership
  const { runId } = useRunSelection(params.periodId ?? null)
  const lifecycle = useLifecycle(clientId)
  const signReach = useSignReach(clientId, runId)
  const groups = useAliasGroups(clientId, runId)
  // one call feeds every zone. state=all&size=500 is the whole run's rows on
  // the reference engagement; a client that outgrows it will page — said here
  // so the cap is a known decision, not a silent truncation.
  const q = useNameMapping(runId, { state: 'all', search: '', sort: 'value', page: 1, size: 500 })
  const [detail, setDetail] = useState<AliasGroup | null>(null)
  const [tab, setTab] = useState('mapping')
  const canRetire = role === 'admin' || role === 'reviewer'

  // ONE handler for approving a group's reach, shared by the banner on the
  // mapping tab and the list on the rules tab. The signature records what the
  // approver actually read, so two copies of this could sign for two
  // different name lists.
  const signReachOf = (id: string, names26: string[], namesBk: string[]) => {
    const g = (groups.data ?? []).find((x) => x.id === id)
    if (g) signReach.mutate({ ...g, names_26as: names26, names_books: namesBk })
  }

  if (runId == null) {
    return <NoRunLoaded surface="name mapping" />
  }
  if (q.isPending) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
  }
  if (q.isError) {
    return (
      <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
        Could not load name mapping: {String(q.error)}
      </p>
    )
  }

  const zones = zonesOf(q.data.items)

  return (
    <div className="space-y-4">
      <MappingHeader summary={q.data.summary} />

      <WithheldBanner
        rows={zones.withheld}
        busy={signReach.isPending}
        onSignReach={signReachOf}
      />

      {/* Two tabs, because the page holds two different KINDS of thing. The
          categories partition the run's parties; an alias rule is not a party
          — it spans several names and often several parties — so listing
          rules underneath them made a rule look like a sixth category. */}
      <div className="flex border-b border-border">
        {[
          { value: 'mapping', label: 'Name mapping' },
          {
            value: 'rules',
            label: `Rules (${zones.decided.mapped.length}${
              zones.decided.retired.length > 0 ? ` · ${zones.decided.retired.length} retired` : ''})`,
          },
        ].map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className={`flex-1 border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.value
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'mapping' && (
        /* The run's whole party population, in the order the Excel's Party
           Reconciliation sheet reads it — as zone TABS, so only the open zone
           renders and the working area starts high. Matching lives inside the
           two one-sided zones, where the names it works on are already
           counted. */
        <CategorySections
          summary={q.data.summary}
          runId={runId}
          clientId={clientId}
          engineJoined={zones.decided.engineJoined}
          suggested={zones.suggested}
          candidates={zones.match}
        />
      )}

      {tab === 'rules' && (
        <RulesTab
          zones={zones}
          busy={lifecycle.isPending || signReach.isPending}
          canRetire={canRetire}
          onLifecycle={(id, verb) => {
            if (!canRetire) {
              showToast({
                color: 'blue',
                title: 'Only a reviewer or an admin can retire a mapping.',
              })
              return
            }
            lifecycle.mutate({ id, verb })
          }}
          onDetail={(id) => setDetail((groups.data ?? []).find((x) => x.id === id) ?? null)}
          onSignReach={signReachOf}
        />
      )}

      {detail && (
        <GroupDetail group={detail} sessionRun={runId} onClose={() => setDetail(null)} />
      )}
    </div>
  )
}
